import { useEffect, useState } from "react";
import { BookOpen, Clock, Eye, Mail, Users, TrendingUp } from "react-feather"
import { minutesIntoReadableFormat, secondsIntoReadableFormat, secondsIntoMinutes, chartTimeFormat, useRemote, useStorage } from "../../Utils/Utils";
import { format } from "date-fns";
import moment from "moment";
import PdfIcon from "../../images/icons/PdfIcon";
import XlsIcon from "../../images/icons/XlsIcon"
import TickerSectorItem from "../../components/TickerSectorItem";
import MapMetrics from "./Components/MapMetrics";
import TopResearchTable from "./Components/TopResearchTable";
import TopUsersTable from "./Components/TopUsersTable";
import TopTickers from "./Components/TopTickers";
import TopAnalystsTable from "./Components/TopAnalystsTable";
import MostViewedAndDownloaded from "./Components/MostViewedAndDownloaded";
import EntryExitPortfolios from "./Components/EntryExitPortfolios";
import SequentialPortfolioViews from "./Components/SequentialPortfolioViews";
import NavigationPatternsViews from "./Components/NavigationPatternsViews";
import TotalMetrics from "./Components/TotalMetrics";
import UsageMetrics from "./Components/UsageMetrics";
import UsageMetricsPortfolio from "./Components/UsageMetricsPortfolio";
import BrowserChart from "./Charts/BrowserChart";
import TopSearches from "./Components/TopSearches";
import TimePeriodComparison from "./Components/TimePeriodComparison";
import DataPoint from "./Components/DataPoint";
import BarChartWrapper from "./Components/BarChartWrapper";
import LineChartWrapper from "./Components/LineChartWrapper";
import AnalyticsToggleHeader from "./Components/AnalyticsToggleHeader";
import AnalyticsHeader from "./Components/AnalyticsHeader";
import TruncatedString from "../Shared/TruncatedString";
import PageContentToggle from "../../components/Menus/PageContentToggle";


export function analyticsExportFilename(slug, customeDate) {
    const safeSlug = slug || "analytics-export";
    if (customeDate && customeDate.from && customeDate.to) {
        const from = moment(customeDate.from).format("YYYY-MM-DD");
        const to = moment(customeDate.to).format("YYYY-MM-DD");
        return `${safeSlug}_${from}_to_${to}.csv`;
    }
    return `${safeSlug}.csv`;
}

// For pages without a banner date picker (Practices/Managers/Portfolios).
// Stamps the file with the day it was downloaded; the aggregate itself is over the full lifetime.
export function analyticsExportFilenameDateless(slug) {
    const safeSlug = slug || "analytics-export";
    const today = moment().format("YYYY-MM-DD");
    return `${safeSlug}_${today}.csv`;
}

export function getMetric(data, metric, parentData) {
    switch (metric.method) {
        case "sort":
            const val = data.toSorted((a, b) => b[metric.column] - a[metric.column])[0]
            const tooltip = val ? metric.tooltip && metric.tooltip.replace("val[metric.column]", val[metric.column]) : null
            switch (metric.display) {
                case "ticker":
                    const newObj = {
                        label: metric.label,
                        tooltip: tooltip,
                        value: val ? val[metric.column] === 0 ? 0 : <TickerSectorItem labelSize="header-1" imageSize={32} label={val?.tickerSymbol} image={`/api/public/images/${val.tickerId ? "ticker" : "group"}/${val?.tickerId ? val.tickerId : val.groupId}.png`} /> : 0
                    }
                    return newObj
                case "minutes":
                    return { label: metric.label, value: val ? minutesIntoReadableFormat(val.minutes) : <h1>-</h1> }
                case "name":
                    const formattedTooltip = metric.tooltipFormat
                        ? metric.tooltipFormat.startsWith("tooltip")
                            ? metric.tooltipFormat.replace("tooltip", tooltip)
                            : <div className="align-items-center"><Clock size={16} className="colour-text-primary pr-xs" />{minutesIntoReadableFormat(tooltip)}</div>
                        : tooltip
                    return val ? { label: metric.label, value: <h1>{val.name}</h1>, tooltip: formattedTooltip } : { label: metric.label, value: <h1>-</h1> }
                default:
                    return { label: metric.label, value: <h1>-</h1> }
            }

        case "length":
            const newObj = {
                label: metric.label,
                value: data.length
            }
            return newObj
        case "avg":
            const totals = metric.totals.map(t => {
                const val = data.reduce((acc, curr) => acc + curr[t], 0)
                return val
            })
            const value = (Math.round(totals[0] / totals[1]))
            const checkedValue = isNaN(value) ? 0 : value
            const formattedValue = metric.format ? minutesIntoReadableFormat(checkedValue) : checkedValue
            return { value: formattedValue, label: metric.label }
        default:
            return <h1>-</h1>
    }
}

export function getNestedValue(datum, string) {
    const arr = string.split(".")
    let value = datum
    arr.map(s => {
        const nested = value[s]
        value = nested
    })
    return value
}

export function displayValueFunction(datum, guidelines) {
    const addings = guidelines?.addings
    switch (guidelines.displayFunction) {
        case "TickerSectorItem":
            return <TickerSectorItem size="m" label={datum.tickerSymbol} extensionLabel={datum.tickerName} image={`/api/public/images/${datum.tickerId ? "ticker" : "group"}/${datum.tickerId ? datum.tickerId : datum.groupId}.png`} />
        case "add":
            return datum[addings[0]] + datum[addings[1]]
        case "minutesIntoReadableFormat":
            return minutesIntoReadableFormat(datum[guidelines.column])
        case "secondsIntoReadableFormat":
            return secondsIntoReadableFormat(datum[guidelines.column])
        case "chartTimeFormat":
            return chartTimeFormat(datum[guidelines.column])
        case "secondsIntoMinutes":
            return secondsIntoMinutes(datum[guidelines.column])
        case "TruncatedString":
            return <TruncatedString string={datum[guidelines.column]} stringStyle={{ paddingRight: guidelines.paddingRight }} />
        case "fullName":
            if (guidelines.nested) {
                return addings.map(e => getNestedValue(datum, e)).join(" ")
            } else {
                return datum[addings[0]] + " " + datum[addings[1]]
            }
        case "objectKeyToObjectValue":
            const keyObj = { [guidelines.keyName]: Object.keys(datum)[0] }
            return keyObj
        case "objectValueToObjectValue":
            const valObj = { [guidelines.keyName]: Object.values(datum)[0] }
            return valObj
        case "date":
            const dateObj = format(new Date(Object.values(datum)[0]), guidelines.format)
            return dateObj
        default:
            return "-"
    }
}

export function getIcon(icon, iconClassName) {
    switch (icon) {
        case "Clock":
            return <Clock className={iconClassName} size={16} />
        case "BookOpen":
            return <BookOpen className={iconClassName} size={16} />
        case "Users":
            return <Users className={iconClassName} size={16} />
        case "Mail":
            return <Mail className={iconClassName} size={16} />
        case "Eye":
            return <Eye className={iconClassName} size={16} />
        case "TrendingUp":
            return <TrendingUp className={iconClassName} size={16} />
        case "PdfIcon":
            return <PdfIcon noHover size={16} />
        case "XlsIcon":
            return <XlsIcon noHover size={16} />
        default:
            break;
    }
}

export function getSingleComponent(c, dateRanges, timeFrame, tab, setTab, centralizedDateRange, customeDate) {
    if (c.showIf && tab && c.showIf !== tab) {
        return null;
    }
    switch (c.name) {
        case "AnalyticsHeader":
            return <AnalyticsHeader children={c.children} mb={c.mb} />
        case "PageContentToggle":
            const labels = c.labels.map(label => ({
                label: label.label,
                active: tab === label.label,
                disabled: label.disabled,
                onClick: () => {
                    if (label.onClick) {
                        label.onClick();
                    } else if (setTab) {
                        setTab(label.label);
                    }
                }
            }));
            return <PageContentToggle labels={labels} style={c.style} />
        case "TotalMetrics":
            return <TotalMetrics data={c.metrics} customeDate={customeDate} />
        case "UsageMetrics":
            return <UsageMetrics
                timeFrame={timeFrame}
                withHeader={c.withHeader}
                alternativeName={c.alternativeName}
                alternativeDateRangers={c.alternativeDateRangers}
                dateRanges={c.withHeader ? dateRanges : null}
                passedMetrics={c.metrics}
                customeDate={customeDate}
            />
        case "UsageMetricsPortfolio":
            return <UsageMetricsPortfolio
                timeFrame={timeFrame}
                withHeader={c.withHeader}
                alternativeName={c.alternativeName}
                passedMetrics={c.metrics}
                mb={c.mb}
                customeDate={customeDate}
            />
        case "MapMetrics":
            return <MapMetrics timeFrame={timeFrame} customeDate={customeDate} />
        case "TopResearchTable":
            return <TopResearchTable dateRanges={dateRanges} customeDate={customeDate} />
        case "TopTickers":
            return <TopTickers dateRanges={dateRanges} customeDate={customeDate} />
        case "TopAnalystsTable":
            return <TopAnalystsTable dateRanges={dateRanges} customeDate={customeDate} />
        case "TopUsersTable":
            return <TopUsersTable dateRanges={dateRanges} customeDate={customeDate} />
        case "EntryExitPortfolios":
            return <EntryExitPortfolios timeFrame={timeFrame} label={c.label} mb={c.mb} entry={c.entry} customeDate={customeDate}/>
        case "SequentialPortfolioViews":
            return <SequentialPortfolioViews timeFrame={timeFrame} label={c.label} mb={c.mb} entry={c.entry} customeDate={customeDate}/>
        case "NavigationPatternsViews":
            return <NavigationPatternsViews timeFrame={timeFrame} label={c.label} mb={c.mb} entry={c.entry} customeDate={customeDate}/>
        case "TopSearches":
            return <TopSearches
                dateRanges={dateRanges}
                label={c.label}
                timeFrame={timeFrame}
                centralizedDateRange={centralizedDateRange}
                mb={c.mb}
                customeDate={customeDate}
            />
        case "MostViewedAndDownloaded":
            return <MostViewedAndDownloaded timeFrame={timeFrame} customeDate={customeDate} />
        case "TimePeriodComparison":
            return <TimePeriodComparison children={c.children} label={c.label} mb={c.mb} customeDate={customeDate} />
        case "DataPoint":
            return <DataPoint children={c.children} label={c.label} mb={c.mb} endpoint={c.endpoint} dataLabel={c.dataLabel} customeDate={customeDate}  />
        case "BarChartWrapper":
            return <BarChartWrapper
                dateRanges={dateRanges}
                label={c.label}
                endpoint={c.endpoint}
                guidelines={c.guidelines}
                barKey1={c.barKey1}
                barKey2={c.barKey2}
                textAnchor={c.textAnchor}
                angle={c.angle}
                textHeight={c.textHeight}
                dx={c.dx}
                dy={c.dy}
                direction={c.direction}
                tail={c.tail}
                removeDatum={c.removeDatum}
                centralizedDateRange={centralizedDateRange}
                passedTimeFrame={timeFrame}
                mb={c.mb}
                axisLabelClass={c.axisLabelClass}
                customeDate={customeDate}
            />
        case "LineChartWrapper":
            return <LineChartWrapper
                dateRanges={dateRanges}
                label={c.label}
                endpoint={c.endpoint}
                guidelines={c.guidelines}
                tooltipGuidelines={c.tooltipGuidelines}
                barKey1={c.barKey1}
                barKey2={c.barKey2}
                dx={c.dx}
                dy={c.dy}
                legends={c.legends}
                legendUnit={c.legendUnit}
                tickWidth={c.tickWidth}
                centralizedDateRange={centralizedDateRange}
                passedTimeFrame={timeFrame}
                yTicksGap={c.yTicksGap}
                mb={c.mb}
                axisLabelClass={c.axisLabelClass}
                customeDate={customeDate}
            />
        case "BrowserChart":
            return <BrowserChart
                options={c.options}
                minMenuWidth={c.minMenuWidth}
                valueStyle={c.valueStyle}
                labelStyle={c.labelStyle}
                height={c.height}
                timeFrame={timeFrame}
                mb={c.mb}
                mt={c.mt}
                gap={c.gap}
                customeDate={customeDate}
            />
        default:
            break;
    }
}

export function applyMethod(obj, data, metrics, totals) {
    switch (obj.method) {
        case "length":
            return { value: data.length }
        case "unique":
            const all = data.map(s => s[obj.column])
            const unique = [...new Set(all)]
            if (obj.lengthOnly) return { value: unique.length, total: unique.length }
            else return { value: unique, total: unique.length }
        case "reduce":
            const column = obj.column
            const total = data.reduce((acc, curr) => curr[column] + acc, 0)
            const formattedTotal = isNaN(total) ? 0 : total
            if (obj.divisor) {
                const divisor = obj.divisor.type === "metrics" ? metrics[obj.divisor.keys] : totals[obj.divisor.index]
                const val = Math.ceil(formattedTotal / divisor)
                const formattedVal = column === "minutes" ? val + " minutes" : val
                return { value: formattedVal, total: total }
            } else return { value: column === "minutes" ? formattedTotal + " minutes" : formattedTotal, total: total }
        case "identity":
            return { value: data }
        default:
            return { value: "-" }
    }
}

export const HeaderTickerDisplay = ({ ticker, property, manager, noLogo }) => {
    const [content, setContent] = useState(<h2>&nbsp;</h2>);
    const remote = useRemote()
    const storage = useStorage()

    useEffect(() => {

        async function fetchContent() {
            if (!ticker) {
                setContent(<h2>&nbsp;</h2>);
                return;
            }
            if (ticker[property] === 0) {
                setContent(<h2>-</h2>);
                return;
            }

            try {
                if (manager) {
                    const fetchedManagerData = await storage.getOrFetch(`funds/portfolio/group/${ticker.groupId}/static-data`);
                    const logo = fetchedManagerData.find(d => d.key === 'pm_logo')
                    const src = logo?.value?.replace("url:", "") || null;

                    setContent(
                        <TickerSectorItem
                            imageClass='manager-logo-image-container'
                            labelSize="header-2"
                            imageSize={src ? null : 32}
                            label={ticker?.groupName}
                            image={src || undefined}
                            noLogo={noLogo}
                        />
                    );
                } else {
                    const fetchedFunfData = ticker.logo ? null : await remote.post(`data/${ticker.tickerSymbol}/get`, ["pm_logo"]);
                    const src = fetchedFunfData?.data?.[0]?.replace("url:", "") || ticker.logo ? ticker.logo : null;

                    setContent(
                        <TickerSectorItem
                            imageClass='manager-logo-image-container'
                            labelSize="header-2"
                            imageSize={src ? null : 32}
                            label={ticker?.tickerName || ticker.name}
                            image={src || undefined}
                        />
                    );
                }

            } catch (err) {
                console.error("Error fetching ticker data:", err);
                setContent(<h2>-</h2>);
            }
        }

        fetchContent();

    }, [ticker, property]);

    return content;
}


const ToggableComponent = ({ dateRanges, options, components, oneWordToggle, centralizedDateRange, timeFrame, customeDate }) => {
    const [selectedDateRange, setSelectedDateRange] = useState(centralizedDateRange ? timeFrame : dateRanges[0])
    const [label, setLabel] = useState(options[0])
    const [selected, setSelected] = useState(components[0])

    useEffect(() => {
        const current = components.find(c => c.label === label)
        setSelected(current)
    }, [label])

    return (
        <>
            <AnalyticsToggleHeader
                value={label}
                values={options}
                setValue={setLabel}
                dateRanges={centralizedDateRange ? null : dateRanges}
                selectedDateRange={centralizedDateRange ? timeFrame : selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
                oneWordToggle={oneWordToggle}
                maxMenuWidth={oneWordToggle ? "100px" : null}
                placement={oneWordToggle ? "bottom-end" : "bottom-start"}
                justifyContent={oneWordToggle ? "center" : null}
                mb={"m"}
            />
            {centralizedDateRange
                ? getSingleComponent(selected, null, timeFrame, null, null, null, customeDate)
                : getSingleComponent(selected, dateRanges, selectedDateRange)
            }
        </>
    )
}

export default ToggableComponent
